import { cloneElement, FC, isValidElement, PropsWithChildren, useEffect, useState, useRef } from 'react';
import { RouteChildrenProps } from 'react-router-dom';
import { WixConnection } from './components/wix-connection';
import { SiteSessionWrapper } from './modules/sites/site-session-wrapper';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primeicons/primeicons.css';
import { Bootstrap } from 'src/redesign/app';
import { MainLayout } from 'src/redesign/layouts';
import { usePostHog } from 'posthog-js/react';
import { Header } from 'src/redesign/features/Header';
import { Tabs } from 'src/redesign/features/Tabs';
import { AuthRouter } from 'src/features/AuthRouter';
import { PublicRouter } from 'src/features/PublicRouter';
import styles from './App.module.scss';
import './styles.scss';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import { Modals } from 'src/redesign/features/Modals';
import { globalReloadingSelectors } from 'src/redesign/shared/store/selectors';
import { Loader } from 'wix-style-react';
import { ToastNotifications } from 'src/redesign/features/Toast';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { HelpBubble } from 'src/redesign/features/HelpBubble';
import { setupTrialLimitInterceptor } from 'src/features/trial-limit-interceptor';
import { setCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { CONNECTED_MODALS } from 'src/redesign/features/Modals/store/constants';
import { selectSettings } from 'src/features/settings-slice';
import { TrialUsageBanner } from 'src/redesign/features/TrialUsageBanner';
import { TrialEndedBanner } from 'src/redesign/features/TrialEndedBanner';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';

declare global {
  interface Window {
    Intercom?: (command: string, ...args: any[]) => void;
    dataLayer?: Array<any>;
  }
}

export const isPublicRoute = () => {
  const path = window.location.href;
  return (
    path.includes('/auth/') ||
    path.includes('/new-event/') ||
    path.includes('/account-created') ||
    path.includes('/email-verification') ||
    path.includes('/self-checkin') ||
    path.includes('/order-frame') ||
    path.includes('/invitations')
  );
};
export const App = () => {
  const account = useAppSelector(accountSelector);
  const selectedSite = useAppSelector(getSelectedSiteDataSelector);
  const settings = useAppSelector(selectSettings);
  const posthog = usePostHog();
  const globalReloading = useAppSelector(globalReloadingSelectors);
  const dispatch = useAppDispatch();
  const approachingModalShownRef = useRef(false);

  // Setup trial limit interceptor on mount
  useEffect(() => {
    setupTrialLimitInterceptor();
  }, []);

  // Check if trial is approaching limit and show modal (80%+)
  useEffect(() => {
    if (!selectedSite || !settings || isPublicRoute() || approachingModalShownRef.current) {
      return;
    }

    // Only show for trial users
    if (settings.plan !== 'trial') {
      return;
    }

    // Don't show if user has already seen the approaching modal (from site field)
    if (selectedSite.has_seen_trial_approaching_modal === true) {
      return;
    }

    // Check if approaching limit (>= 80%)
    const percentageUsed = selectedSite.trial_percentage_used || 0;
    const orderCount = selectedSite.trial_order_count || 0;
    const orderLimit = selectedSite.trial_order_limit || 10;

    // Show approaching modal at 80%+, but not at 100% (hard stop modal handles that)
    if (percentageUsed >= 80 && orderCount < orderLimit) {
      approachingModalShownRef.current = true;
      // Small delay to let page load first
      const timer = setTimeout(() => {
        dispatch(
          setCurrentModalAction({
            currentModal: CONNECTED_MODALS.TRIAL_APPROACHING_LIMIT_MODAL,
            data: {
              title: 'Approaching Trial Limit',
              currentOrderCount: orderCount,
              orderLimit: orderLimit,
              percentageUsed: percentageUsed,
            },
            visible: true,
          }),
        );
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [selectedSite?.id, settings?.plan, dispatch]); // Only track ID and plan, not whole objects

  // Note: We removed the auto-show modal for 100% trial limit
  // because we now have TrialEndedBanner that shows the options to the user.
  // The modal will only show when user clicks "Upgrade" from the banner or when
  // the trial limit interceptor catches an API error.

  useEffect(() => {
    if (account?.email) {
      posthog?.identify(account.email, {
        email: account.email,
      });
    }
  }, [account, posthog]);

  // Track Shopify sign-up on first load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isNewAccount = urlParams.get('isNewAccount');

    if (isNewAccount === 'true' && account?.email) {
      // Track new user signup with Google Analytics / GTM
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'sign_up',
          method: 'shopify',
        });
      }

      // Clean up URL by removing the isNewAccount parameter
      urlParams.delete('isNewAccount');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [account]);

  if (
    process.env.NODE_ENV === 'production' &&
    window.brand === 'wix' &&
    !account?.has_app_updated
  ) {
    return <WixConnection />;
  }

  return (
    <Bootstrap>
      <HelpBubble />
      <Dialog
        header="Account Disabled"
        visible={account?.is_disabled === true}
        closable={false}
        draggable={false}
        resizable={false}
        style={{ width: '450px' }}
        modal
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: '15px', lineHeight: '1.6' }}>
            Your account has been disabled due to being against our terms.
          </p>
          <p style={{ lineHeight: '1.6' }}>
            Please contact support at{' '}
            <a href="mailto:support@ticketspotapp.com" style={{ color: '#007bff' }}>
              support@ticketspotapp.com
            </a>{' '}
            if this is incorrect.
          </p>
        </div>
      </Dialog>
      <ConfirmDialog draggable={false} />
      {account && !isPublicRoute() ? (
        <>
          {/* <TicketPacksBanner /> */}
          <TrialEndedBanner />
          <TrialUsageBanner />
          <Header />
          <Tabs />
          <MainLayout>{globalReloading ? <Loader size="small" /> : <AuthRouter />}</MainLayout>
          <Modals />
          <ToastNotifications />
        </>
      ) : (
        <div className={styles.publicPageWrapper}>
          <PublicRouter />
          <ToastNotifications />
        </div>
      )}
    </Bootstrap>
  );

  // return (
  //   <div className="App">
  //     {window.brand === 'wix' && account.newsletter_opt_in === null && (
  //       <NewsletterOptIn account={account} />
  //     )}
  //     {/* {account.display_review_offer === null && <LeaveReview account={account} />} */}

  //   </div>
  // );
};

export const SiteWrapper: FC<PropsWithChildren<RouteChildrenProps & { noAuth?: boolean }>> = (
  props,
) => {
  const { children, match, noAuth } = props;
  const params = match.params as { site_id?: string };
  return (
    <SiteSessionWrapper siteId={params?.site_id} noAuth={noAuth}>
      {isValidElement(children) && cloneElement(children, { ...props })}
    </SiteSessionWrapper>
  );
};
